// Payment Gateway Integration
import { supabase } from '../lib/supabaseClient'

export class PaymentService {
  static async processPayment(paymentData) {
    const { method, amount, orderId, customerInfo } = paymentData
    
    try {
      switch (method) {
        case 'stripe':
          return await this.processStripePayment(paymentData)
        case 'bkash':
          return await this.processBkashPayment(paymentData)
        case 'nagad':
          return await this.processNagadPayment(paymentData)
        case 'paypal':
          return await this.processPayPalPayment(paymentData)
        default:
          throw new Error('Unsupported payment method')
      }
    } catch (error) {
      console.error('Payment processing error:', error)
      throw error
    }
  }

  static async processStripePayment(paymentData) {
    // Stripe payment processing
    const { amount, orderId, customerInfo } = paymentData
    
    // Create payment intent
    const { data, error } = await supabase.functions.invoke('stripe-payment', {
      body: {
        amount: amount * 100, // Convert to cents
        currency: 'bdt',
        orderId,
        customerInfo
      }
    })

    if (error) throw error
    return data
  }

  static async processBkashPayment(paymentData) {
    const { amount, orderId, customerInfo } = paymentData
    
    // bKash payment processing via Edge Function
    const { data, error } = await supabase.functions.invoke('bkash-payment', {
      body: {
        amount,
        orderId,
        customerInfo,
        callbackURL: `${window.location.origin}/payment/callback`
      }
    })

    if (error) throw error
    return data
  }

  static async processNagadPayment(paymentData) {
    const { amount, orderId, customerInfo } = paymentData
    
    // Nagad payment processing via Edge Function
    const { data, error } = await supabase.functions.invoke('nagad-payment', {
      body: {
        amount,
        orderId,
        customerInfo,
        callbackURL: `${window.location.origin}/payment/callback`
      }
    })

    if (error) throw error
    return data
  }

  static async processPayPalPayment(paymentData) {
    const { amount, orderId, customerInfo } = paymentData
    
    // PayPal payment processing via Edge Function
    const { data, error } = await supabase.functions.invoke('paypal-payment', {
      body: {
        amount,
        currency: 'USD',
        orderId,
        customerInfo,
        returnURL: `${window.location.origin}/payment/success`,
        cancelURL: `${window.location.origin}/payment/cancel`
      }
    })

    if (error) throw error
    return data
  }

  static async verifyPayment(paymentId, method) {
    const { data, error } = await supabase.functions.invoke('verify-payment', {
      body: {
        paymentId,
        method
      }
    })

    if (error) throw error
    return data
  }

  static async createPaymentRecord(orderId, paymentData) {
    const { data, error } = await supabase
      .from('payments')
      .insert({
        order_id: orderId,
        amount: paymentData.amount,
        method: paymentData.method,
        status: 'pending',
        transaction_id: paymentData.transactionId,
        gateway_response: paymentData.gatewayResponse
      })
      .select()
      .single()

    if (error) throw error
    return data
  }

  static async updatePaymentStatus(paymentId, status, transactionId = null) {
    const updateData = { status }
    if (transactionId) updateData.transaction_id = transactionId

    const { data, error } = await supabase
      .from('payments')
      .update(updateData)
      .eq('id', paymentId)
      .select()
      .single()

    if (error) throw error
    return data
  }
}

// Payment Gateway Components
export const PaymentMethodSelector = ({ selectedMethod, onMethodChange, methods = ['stripe', 'bkash', 'nagad', 'paypal'] }) => {
  const methodConfig = {
    stripe: {
      name: 'Credit/Debit Card',
      icon: '💳',
      description: 'Pay with Visa, Mastercard, or American Express',
      color: 'bg-blue-600'
    },
    bkash: {
      name: 'bKash',
      icon: '📱',
      description: 'Pay with your bKash mobile wallet',
      color: 'bg-pink-600'
    },
    nagad: {
      name: 'Nagad',
      icon: '📱',
      description: 'Pay with your Nagad mobile wallet',
      color: 'bg-green-600'
    },
    paypal: {
      name: 'PayPal',
      icon: '🌐',
      description: 'Pay with PayPal account',
      color: 'bg-blue-500'
    }
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold mb-4">Payment Method</h3>
      {methods.map((method) => (
        <label key={method} className="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50">
          <input
            type="radio"
            name="paymentMethod"
            value={method}
            checked={selectedMethod === method}
            onChange={(e) => onMethodChange(e.target.value)}
            className="mr-4"
          />
          <div className={`w-10 h-10 ${methodConfig[method].color} rounded-lg flex items-center justify-center mr-4`}>
            <span className="text-white text-lg">{methodConfig[method].icon}</span>
          </div>
          <div className="flex-1">
            <div className="font-medium text-gray-900">{methodConfig[method].name}</div>
            <div className="text-sm text-gray-600">{methodConfig[method].description}</div>
          </div>
        </label>
      ))}
    </div>
  )
}

export const PaymentForm = ({ method, onSubmit, loading = false }) => {
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    onSubmit(formData)
  }

  const renderMethodForm = () => {
    switch (method) {
      case 'stripe':
        return (
          <div className="space-y-4">
            <div>
              <label className="label">Card Number</label>
              <input
                name="cardNumber"
                value={formData.cardNumber || ''}
                onChange={handleChange}
                className="input-field"
                placeholder="1234 5678 9012 3456"
                maxLength="19"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Expiry Date</label>
                <input
                  name="expiryDate"
                  value={formData.expiryDate || ''}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="MM/YY"
                  maxLength="5"
                />
              </div>
              <div>
                <label className="label">CVV</label>
                <input
                  name="cvv"
                  value={formData.cvv || ''}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="123"
                  maxLength="4"
                />
              </div>
            </div>
            <div>
              <label className="label">Cardholder Name</label>
              <input
                name="cardholderName"
                value={formData.cardholderName || ''}
                onChange={handleChange}
                className="input-field"
                placeholder="John Doe"
              />
            </div>
          </div>
        )

      case 'bkash':
        return (
          <div className="space-y-4">
            <div>
              <label className="label">bKash Mobile Number</label>
              <input
                name="mobileNumber"
                value={formData.mobileNumber || ''}
                onChange={handleChange}
                className="input-field"
                placeholder="01XXXXXXXXX"
                maxLength="11"
              />
            </div>
            <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
              <p className="text-sm text-pink-800">
                <strong>Note:</strong> You will receive a bKash payment request on your mobile. 
                Please complete the payment using your bKash PIN.
              </p>
            </div>
          </div>
        )

      case 'nagad':
        return (
          <div className="space-y-4">
            <div>
              <label className="label">Nagad Mobile Number</label>
              <input
                name="mobileNumber"
                value={formData.mobileNumber || ''}
                onChange={handleChange}
                className="input-field"
                placeholder="01XXXXXXXXX"
                maxLength="11"
              />
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-800">
                <strong>Note:</strong> You will receive a Nagad payment request on your mobile. 
                Please complete the payment using your Nagad PIN.
              </p>
            </div>
          </div>
        )

      case 'paypal':
        return (
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> You will be redirected to PayPal to complete your payment. 
                After successful payment, you will be redirected back to our website.
              </p>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {renderMethodForm()}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full"
      >
        {loading ? (
          <div className="flex items-center justify-center">
            <div className="loading-spinner mr-2"></div>
            Processing Payment...
          </div>
        ) : (
          'Complete Payment'
        )}
      </button>
    </form>
  )
}

export const PaymentStatus = ({ status, transactionId, amount, method }) => {
  const statusConfig = {
    pending: {
      icon: '⏳',
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-100',
      title: 'Payment Pending',
      message: 'Your payment is being processed. Please wait...'
    },
    success: {
      icon: '✅',
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      title: 'Payment Successful',
      message: 'Your payment has been processed successfully!'
    },
    failed: {
      icon: '❌',
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      title: 'Payment Failed',
      message: 'Your payment could not be processed. Please try again.'
    },
    cancelled: {
      icon: '🚫',
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      title: 'Payment Cancelled',
      message: 'You have cancelled the payment process.'
    }
  }

  const config = statusConfig[status] || statusConfig.pending

  return (
    <div className={`${config.bgColor} border border-gray-200 rounded-lg p-6 text-center`}>
      <div className={`text-4xl mb-4 ${config.color}`}>
        {config.icon}
      </div>
      <h3 className={`text-xl font-semibold mb-2 ${config.color}`}>
        {config.title}
      </h3>
      <p className="text-gray-700 mb-4">{config.message}</p>
      
      {transactionId && (
        <div className="text-sm text-gray-600">
          <p><strong>Transaction ID:</strong> {transactionId}</p>
          <p><strong>Amount:</strong> ৳{amount?.toLocaleString()}</p>
          <p><strong>Method:</strong> {method}</p>
        </div>
      )}
    </div>
  )
}
